import React, { type FC, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { FileEarmarkSpreadsheet, Upload, X } from 'react-bootstrap-icons';

import { usePostCartImportPreview, usePutCartImport } from 'api';
import type {
  ICartImportPreviewInvalidRow,
  ICartImportPreviewSummary,
  ICartImportPreviewValidRow,
} from 'api/endpoints/usePostCartImportPreview';

import CartImportPreviewSummary from 'components/wise/WiseCartImportModal/components/CartImportPreviewSummary';
import styles from 'theme/components/wise/WiseCartImportModal/WiseCartImportModal.module.scss';

type Mode = 'idle' | 'previewing' | 'preview' | 'submitting';

export type CartImportCsvTabProps = {
  cartId: number;
  onSuccess: () => void;
};

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        const parts = result.split(',');
        resolve(parts[1] ?? result);
      } else {
        reject(new Error('Cannot read file'));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// Legacy PUT /carts/{id}/import oczekuje 3 kolumn (KTM;Ilosc;Jednostka) i sam zdejmuje pierwszy wiersz.
// Nasz nowy format (export + uploadowany przez usera) to KTM;Jednostka;Ilosc;[reszta informacyjna].
// Przed commitem zostawiamy 3 wymagane kolumny i przestawiamy na kolejnosc legacy (KTM;Ilosc;Jednostka).
const stripToLegacyFormat = (base64: string): string => {
  const decoded = decodeURIComponent(escape(atob(base64)));
  const lines = decoded.split(/\r?\n/).filter((line) => line.trim() !== '');
  const transformed = lines.map((line) => {
    const cells: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ';' && !inQuotes) {
        cells.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    cells.push(current);
    if (cells.length >= 3) {
      // Z naszego formatu KTM;Jednostka;Ilosc;... na legacy KTM;Ilosc;Jednostka
      return [cells[0], cells[2], cells[1]].join(';');
    }
    return cells.join(';');
  });
  return btoa(unescape(encodeURIComponent(transformed.join('\n') + '\n')));
};

const CartImportCsvTab: FC<CartImportCsvTabProps> = ({ cartId, onSuccess }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<Mode>('idle');
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileBase64, setFileBase64] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<{
    valid: ICartImportPreviewValidRow[];
    invalid: ICartImportPreviewInvalidRow[];
    summary: ICartImportPreviewSummary;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const previewMutation = usePostCartImportPreview(cartId, {
    onSuccess: (response) => {
      setPreviewData(response.data);
      setMode('preview');
    },
    onError: () => {
      setErrorMessage(t('Nie udalo sie wczytac pliku'));
      setMode('idle');
    },
  });

  const commitMutation = usePutCartImport(cartId, {
    onSuccess: () => {
      onSuccess();
    },
    onError: () => {
      setErrorMessage(t('Nie udalo sie wykonac importu'));
      setMode('preview');
    },
  });

  const resetFile = useCallback(() => {
    setFileBase64(null);
    setFileName(null);
    setPreviewData(null);
    setErrorMessage(null);
    setMode('idle');
  }, []);

  const handleFile = useCallback(
    async (file: File) => {
      try {
        setErrorMessage(null);
        const base64 = await readFileAsBase64(file);
        setFileBase64(base64);
        setFileName(file.name);
        setMode('previewing');
        previewMutation.mutate({ file: base64 });
      } catch (err) {
        setErrorMessage(t('Nie udalo sie wczytac pliku'));
      }
    },
    [previewMutation, t]
  );

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      void handleFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      void handleFile(file);
    }
  };

  const handleConfirm = () => {
    if (!fileBase64 || !fileName) return;
    setMode('submitting');
    const legacyFormatBase64 = stripToLegacyFormat(fileBase64);
    commitMutation.mutate({ file: legacyFormatBase64, name: fileName });
  };

  return (
    <div className={styles.modalBody}>
      {mode === 'idle' && (
        <div
          className={classnames(styles.dropzone, { [styles.dropzoneActive]: isDragging })}
          onDragOver={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}>
          <div className={styles.dropzoneIcon}>
            <Upload />
          </div>
          <div className={styles.dropzoneTitle}>
            {t('Przeciagnij plik tutaj lub kliknij, aby wybrac')}
          </div>
          <div className={styles.dropzoneHint}>CSV, XLSX, XLS</div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            onChange={handleFileInputChange}
            hidden
          />
        </div>
      )}

      {mode === 'idle' && (
        <div className={styles.formatHelp}>
          <div className={styles.formatHelpTitle}>
            {t('Jak powinien wygladac plik?')}
          </div>
          <ul className={styles.formatHelpList}>
            <li>
              {t(
                'Plik CSV z separatorem srednika ; (lub XLSX/XLS). Pierwszy wiersz to naglowek.'
              )}
            </li>
            <li>
              <strong>{t('Brane pod uwage sa tylko 3 pierwsze kolumny:')}</strong>{' '}
              {t('KTM, Jednostka, Ilosc (w tej kolejnosci).')}
            </li>
            <li>
              {t(
                'W kolumnie KTM mozesz podac: symbol produktu, kod EAN-13, kod z systemu ERP albo ID wewnetrzne.'
              )}
            </li>
            <li>
              {t(
                'Kolumny posrednie (np. Nazwa, cena) sa ignorowane - to tylko informacja dla Ciebie po eksporcie.'
              )}
            </li>
            <li>
              {t(
                'Jednostka musi byc dostepna dla produktu (np. szt, kpl, wpal).'
              )}
            </li>
          </ul>
          <div className={styles.formatHelpExample}>
            {`KTM;Jednostka;Ilosc
DHP490Z;szt;1
5902628003331;szt;2
EVO-R210SMSP;szt;3`}
          </div>
        </div>
      )}

      {mode === 'previewing' && (
        <div className={styles.loading}>{t('Analizowanie pliku...')}</div>
      )}

      {(mode === 'preview' || mode === 'submitting') && previewData && fileName && (
        <>
          <div className={styles.fileCard}>
            <FileEarmarkSpreadsheet />
            <div>
              <div className={styles.fileName}>{fileName}</div>
              <div className={styles.fileMeta}>{t('Podglad importu')}</div>
            </div>
            <button
              type="button"
              className={styles.actionButton}
              onClick={resetFile}
              aria-label={t('Usun plik')}>
              <X />
            </button>
          </div>

          <CartImportPreviewSummary
            valid={previewData.valid}
            invalid={previewData.invalid}
            summary={previewData.summary}
          />

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.actionButton}
              onClick={resetFile}
              disabled={mode === 'submitting'}>
              {t('Anuluj')}
            </button>
            <button
              type="button"
              className={classnames(styles.actionButton, styles.primary)}
              onClick={handleConfirm}
              disabled={
                mode === 'submitting' || previewData.summary.valid_count === 0
              }>
              {t('Potwierdz import')}
            </button>
          </div>
        </>
      )}

      {errorMessage && <div className={styles.invalidTitle}>{errorMessage}</div>}
    </div>
  );
};

export default CartImportCsvTab;
